import {
    centsToFormattedDollars,
    formattedDollarsToCents,
} from '@fruitful/fruitils';
import { Redirect, router, useFocusEffect } from 'expo-router';
import { useAtom } from 'jotai';
import * as React from 'react';
import { useRef, useState } from 'react';
import type { TextInput } from 'react-native';
import {
    BodyMediumEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    DollarInput,
    FootnoteRegular,
    FruitfulCustomIcon,
    NavigationTile,
    ScreenContainer,
    XStack,
    YStack,
} from 'smoothie';

import { assertNotNullish } from '../../../common/misc';
import { getLastFourAccountNumber } from '../../../common/utils/bankingUtils';
import { useGetApexAccounts } from '../../hooks/useGetApexAccounts';
import { apexDepositRequestAtom } from '../../state/investmentAtoms';
import {
    BackdoorRothIctDepositWarningNotice,
    shouldWarnBackdoorRothIctDeposit,
} from './BackdoorRothIctDepositWarningNotice';
import {
    BackdoorTradIctDepositConversionNotice,
    shouldShowBackdoorTradConversionNotice,
} from './BackdoorTradIctDepositConversionNotice';

export const DepositAmount = () => {
    const inputRef = useRef<TextInput>(null);
    useFocusEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    });

    const [apexDepositRequest, setApexDepositRequest] = useAtom(
        apexDepositRequestAtom,
    );

    const [amount, setAmount] = useState<string | undefined>();

    const { data: apexAccounts } = useGetApexAccounts();

    if (apexDepositRequest?.toAccount == null) {
        return <Redirect href="/home/investment" />;
    }

    if (apexDepositRequest?.fromAccount == null) {
        return <Redirect href="/home/investment" />;
    }

    const { contributionYear, fromAccount, toAccount } = apexDepositRequest;

    const availableBalance = fromAccount.availableBalance ?? 0;
    const availableBalanceFormatted = centsToFormattedDollars(availableBalance);

    const contributionSoFar = toAccount.contributionSummaries.find(
        (s) => assertNotNullish(s.taxYear) === contributionYear,
    );

    // TODO update this comment when we get an answer
    //   https://support.apexfintechsolutions.com/hc/requests/1132406
    // Per the Apex documentation a zero value here means that there is no
    // contribution limit.  In pracice we have found that Apex returns null
    // rather than zero but there is an open ticket about this.
    // This is relevant to SEP IRAs and possibly other account types.
    const allowedAmount =
        contributionSoFar?.remainingContributionAllowed?.value != null
            ? parseFloat(
                  contributionSoFar?.remainingContributionAllowed?.value,
              ) * 100
            : Infinity;

    const amountToTransferCents = formattedDollarsToCents(amount ?? '0.00');

    const isRetirementAccount = toAccount.isRetirementAccount;
    const isDisabled =
        isNaN(amountToTransferCents) ||
        amountToTransferCents <= 0 ||
        amountToTransferCents > availableBalance ||
        (isRetirementAccount ? amountToTransferCents > allowedAmount : false);

    return (
        <ScreenContainer
            onFallbackRoute="/home/investment"
            title="Amount"
            hasBackButton={true}
        >
            <YStack gap={24} padding={2}>
                {shouldWarnBackdoorRothIctDeposit(toAccount) && (
                    <BackdoorRothIctDepositWarningNotice />
                )}
                {shouldShowBackdoorTradConversionNotice(toAccount) && (
                    <BackdoorTradIctDepositConversionNotice
                        traditionalIraAccount={toAccount}
                        apexAccounts={apexAccounts}
                    />
                )}
                <DollarInput
                    ref={inputRef}
                    marginVertical={64}
                    allowDecimals={true}
                    value={amount}
                    onChangeText={(amt) => {
                        setAmount(amt);
                    }}
                    placeholder="$0.00"
                />
                <YStack
                    gap={16}
                    borderRadius={16}
                    backgroundColor="$gray200"
                    padding={32}
                    justifyContent="center"
                >
                    <XStack
                        justifyContent="space-between"
                        gap={16}
                        alignItems="center"
                    >
                        <XStack gap={8} alignItems="center" flexShrink={1}>
                            <FruitfulCustomIcon
                                icon={fromAccount.icon}
                                color={fromAccount.color}
                            />
                            <YStack flexShrink={1}>
                                <BodyMediumEmphasized>
                                    {fromAccount.nickname}
                                </BodyMediumEmphasized>
                                <FootnoteRegular color="$gray1000">
                                    {fromAccount.bankName} *
                                    {getLastFourAccountNumber(
                                        fromAccount.accountNumberMasked,
                                    )}
                                </FootnoteRegular>
                            </YStack>
                        </XStack>
                        <YStack alignItems="flex-end">
                            <BodyMediumEmphasized>
                                {availableBalanceFormatted}
                            </BodyMediumEmphasized>
                            <FootnoteRegular color="$gray1000">
                                available balance
                            </FootnoteRegular>
                        </YStack>
                    </XStack>
                    {isRetirementAccount && allowedAmount < Infinity && (
                        <XStack
                            justifyContent="space-between"
                            gap={16}
                            alignItems="center"
                        >
                            <XStack gap={8} alignItems="center" flexShrink={1}>
                                <FruitfulCustomIcon
                                    icon={toAccount.icon}
                                    color={toAccount.color}
                                    useAltColors={true}
                                />
                                <YStack flexShrink={1}>
                                    <BodyMediumEmphasized>
                                        Remaining {contributionYear}
                                    </BodyMediumEmphasized>
                                    <BodyMediumEmphasized>
                                        contribution limit
                                    </BodyMediumEmphasized>
                                </YStack>
                            </XStack>
                            <BodyMediumEmphasized>
                                {centsToFormattedDollars(allowedAmount)}
                            </BodyMediumEmphasized>
                        </XStack>
                    )}
                    {isRetirementAccount && allowedAmount === Infinity && (
                        <NavigationTile
                            title="Contributions to this IRA are not tracked against IRS limits within this platform. It is your responsibility to ensure your total contributions do not exceed the annual limit set by the IRS. Exceeding this limit may result in tax penalties. Please consult a tax advisor or contact your Guide with any questions."
                            tone="positive"
                        />
                    )}
                </YStack>
                <Button
                    disabled={isDisabled}
                    text="Continue"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={() => {
                        setApexDepositRequest({
                            ...apexDepositRequest,
                            amountCents: amountToTransferCents,
                        });
                        router.navigate('/investments/transfer/deposit-review');
                    }}
                />
            </YStack>
        </ScreenContainer>
    );
};
